import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../../components/ui/card";
import { TrendingUp, TrendingDown, Calendar, DollarSign } from "lucide-react";

const MonthlyBudgetTrendsChart = ({ data, loading }) => {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Tendencias Presupuestales Mensuales</CardTitle>
          <CardDescription>Cargando datos...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Tendencias Presupuestales Mensuales</CardTitle>
          <CardDescription>No hay datos disponibles</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            <p>No hay datos de tendencias presupuestales disponibles</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Procesar datos para el gráfico
  const chartData = data.map((item) => ({
    month: item.month,
    budget: parseFloat(item.budget_amount || 0),
    spent: parseFloat(item.spent_amount || 0),
    remaining: parseFloat(item.remaining_amount || 0),
    executionRate: parseFloat(item.execution_rate || 0),
    variance: parseFloat(item.variance || 0),
    currency: item.currency || "USD",
  }));

  // Calcular estadísticas generales
  const totalBudget = chartData.reduce((sum, item) => sum + item.budget, 0);
  const totalSpent = chartData.reduce((sum, item) => sum + item.spent, 0);
  const totalRemaining = chartData.reduce(
    (sum, item) => sum + item.remaining,
    0
  );
  const averageExecutionRate =
    chartData.length > 0
      ? chartData.reduce((sum, item) => sum + item.executionRate, 0) /
        chartData.length
      : 0;

  // Calcular tendencia
  const firstMonth = chartData[0];
  const lastMonth = chartData[chartData.length - 1];
  const budgetTrend =
    firstMonth && lastMonth && firstMonth.budget > 0
      ? ((lastMonth.budget - firstMonth.budget) / firstMonth.budget) * 100
      : 0;
  const spendingTrend =
    firstMonth && lastMonth && firstMonth.spent > 0
      ? ((lastMonth.spent - firstMonth.spent) / firstMonth.spent) * 100
      : 0;

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (percentage) => {
    return `${Math.round(percentage)}%`;
  };

  return (
    <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-slate-50 dark:from-slate-800 dark:to-slate-900">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center">
            <Calendar className="w-5 h-5 text-white" />
          </div>
          <div>
            <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
              Tendencias Presupuestales Mensuales
            </CardTitle>
            <CardDescription className="text-slate-600 dark:text-slate-400">
              Evolución del presupuesto asignado vs gastado por mes
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Estadísticas generales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <DollarSign className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                Presupuesto Total
              </span>
            </div>
            <p className="text-lg font-bold text-blue-700 dark:text-blue-300">
              {formatCurrency(totalBudget)}
            </p>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <TrendingDown className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              <span className="text-sm font-medium text-orange-600 dark:text-orange-400">
                Gastado Total
              </span>
            </div>
            <p className="text-lg font-bold text-orange-700 dark:text-orange-300">
              {formatCurrency(totalSpent)}
            </p>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-green-600 dark:text-green-400">
                Restante Total
              </span>
            </div>
            <p className="text-lg font-bold text-green-700 dark:text-green-300">
              {formatCurrency(totalRemaining)}
            </p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Calendar className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                Ejecución Promedio
              </span>
            </div>
            <p className="text-lg font-bold text-purple-700 dark:text-purple-300">
              {formatPercentage(averageExecutionRate)}
            </p>
          </div>
        </div>

        {/* Indicadores de tendencia */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div
            className={`p-3 rounded-lg ${
              budgetTrend >= 0
                ? "bg-green-50 dark:bg-green-900/20"
                : "bg-red-50 dark:bg-red-900/20"
            }`}
          >
            <div className="flex items-center gap-2">
              {budgetTrend >= 0 ? (
                <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-600 dark:text-red-400" />
              )}
              <span
                className={`text-sm font-medium ${
                  budgetTrend >= 0
                    ? "text-green-600 dark:text-green-400"
                    : "text-red-600 dark:text-red-400"
                }`}
              >
                Tendencia Presupuesto
              </span>
            </div>
            <p
              className={`text-lg font-bold ${
                budgetTrend >= 0
                  ? "text-green-700 dark:text-green-300"
                  : "text-red-700 dark:text-red-300"
              }`}
            >
              {budgetTrend >= 0 ? "+" : ""}
              {formatPercentage(budgetTrend)}
            </p>
          </div>
          <div
            className={`p-3 rounded-lg ${
              spendingTrend >= 0
                ? "bg-orange-50 dark:bg-orange-900/20"
                : "bg-blue-50 dark:bg-blue-900/20"
            }`}
          >
            <div className="flex items-center gap-2">
              {spendingTrend >= 0 ? (
                <TrendingUp className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              ) : (
                <TrendingDown className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              )}
              <span
                className={`text-sm font-medium ${
                  spendingTrend >= 0
                    ? "text-orange-600 dark:text-orange-400"
                    : "text-blue-600 dark:text-blue-400"
                }`}
              >
                Tendencia Gasto
              </span>
            </div>
            <p
              className={`text-lg font-bold ${
                spendingTrend >= 0
                  ? "text-orange-700 dark:text-orange-300"
                  : "text-blue-700 dark:text-blue-300"
              }`}
            >
              {spendingTrend >= 0 ? "+" : ""}
              {formatPercentage(spendingTrend)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gráfico de líneas - Presupuesto vs Gastado */}
          <div className="h-80">
            <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
              Presupuesto vs Gastado
            </h4>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={{ stroke: "#e2e8f0" }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white dark:bg-slate-800 p-4 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl">
                          <p className="font-semibold text-slate-900 dark:text-slate-100 mb-2">
                            {data.month}
                          </p>
                          <div className="space-y-1">
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              <span className="font-medium">Presupuesto:</span>{" "}
                              {formatCurrency(data.budget, data.currency)}
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              <span className="font-medium">Gastado:</span>{" "}
                              {formatCurrency(data.spent, data.currency)}
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              <span className="font-medium">Restante:</span>{" "}
                              {formatCurrency(data.remaining, data.currency)}
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              <span className="font-medium">Ejecución:</span>{" "}
                              {formatPercentage(data.executionRate)}
                            </p>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="budget"
                  name="Presupuesto"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  dot={{ fill: "#3b82f6", strokeWidth: 2, r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="spent"
                  name="Gastado"
                  stroke="#f59e0b"
                  strokeWidth={3}
                  dot={{ fill: "#f59e0b", strokeWidth: 2, r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="remaining"
                  name="Restante"
                  stroke="#10b981"
                  strokeWidth={3}
                  dot={{ fill: "#10b981", strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Gráfico de área - Tasa de ejecución */}
          <div className="h-80">
            <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
              Tasa de Ejecución Mensual
            </h4>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient
                    id="executionGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={{ stroke: "#e2e8f0" }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white dark:bg-slate-800 p-4 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl">
                          <p className="font-semibold text-slate-900 dark:text-slate-100 mb-2">
                            {data.month}
                          </p>
                          <div className="space-y-1">
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              <span className="font-medium">
                                Tasa de Ejecución:
                              </span>{" "}
                              {formatPercentage(data.executionRate)}
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              <span className="font-medium">Varianza:</span>{" "}
                              {formatPercentage(data.variance)}
                            </p>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="executionRate"
                  name="Tasa de Ejecución"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  fill="url(#executionGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Gráfico de barras - Comparación mensual */}
        <div className="mt-6">
          <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
            Comparación Mensual Detallada
          </h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={{ stroke: "#e2e8f0" }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white dark:bg-slate-800 p-4 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl">
                          <p className="font-semibold text-slate-900 dark:text-slate-100 mb-2">
                            {data.month}
                          </p>
                          <div className="space-y-1">
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              <span className="font-medium">Presupuesto:</span>{" "}
                              {formatCurrency(data.budget, data.currency)}
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              <span className="font-medium">Gastado:</span>{" "}
                              {formatCurrency(data.spent, data.currency)}
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              <span className="font-medium">Restante:</span>{" "}
                              {formatCurrency(data.remaining, data.currency)}
                            </p>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Bar
                  dataKey="budget"
                  name="Presupuesto"
                  fill="#3b82f6"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="spent"
                  name="Gastado"
                  fill="#f59e0b"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="remaining"
                  name="Restante"
                  fill="#10b981"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MonthlyBudgetTrendsChart;
